<script>
  import { onMount } from 'svelte';
  import Layout from '../components/Layout.svelte';
  import ReportViewer from '../components/ReportViewer.svelte';
  import { customerReportsApi, customersApi } from '../stores/api.js';
  import { isAdmin, isCustomer } from '../stores/roles.js';
  import { _ } from '../lib/i18n.js';
  import {
    FileText, Upload, Trash2, Eye, Loader, CheckCircle, AlertCircle,
    Search, Filter, Calendar, User, X
  } from 'lucide-svelte';

  let loading = true;
  let uploading = false;
  let message = null;
  let reports = [];
  let customers = [];
  let loadingCustomers = false;

  // Filter state
  let filterCustomerId = '';

  // Upload form state
  let uploadName = '';
  let uploadCustomerId = '';
  let uploadFile = null;
  let fileInputRef = null;

  // Report viewer state
  let viewingReport = null;
  let loadingReport = false;

  // Delete confirmation state
  let deletingReportId = null;

  onMount(() => {
    loadReports();
    if ($isAdmin) {
      loadCustomers();
    }
  });

  async function loadReports() {
    loading = true;
    try {
      console.log('[Reports] Loading reports...');
      let response;

      if ($isCustomer) {
        // Customer view: load own reports
        response = await customerReportsApi.listMy();
      } else {
        // Admin view: load all reports with optional filter
        response = await customerReportsApi.list(filterCustomerId || null);
      }

      reports = response.reports || [];
      console.log(`[Reports] Loaded ${reports.length} reports`);
    } catch (err) {
      console.error('[Reports] Error loading reports:', err);
      message = { type: 'error', text: err.message };
    } finally {
      loading = false;
    }
  }

  async function loadCustomers() {
    loadingCustomers = true;
    try {
      const response = await customersApi.getAll();
      customers = response.customers || [];
      console.log(`[Reports] Loaded ${customers.length} customers for dropdown`);
    } catch (err) {
      console.error('[Reports] Error loading customers:', err);
    } finally {
      loadingCustomers = false;
    }
  }

  function handleFilterChange() {
    loadReports();
  }

  function handleFileChange(event) {
    const file = event.target.files?.[0];
    if (file) {
      uploadFile = file;
      // Auto-fill name from filename if empty
      if (!uploadName) {
        uploadName = file.name.replace(/\.md$/i, '');
      }
    }
  }

  function clearUploadForm() {
    uploadName = '';
    uploadCustomerId = '';
    uploadFile = null;
    if (fileInputRef) {
      fileInputRef.value = '';
    }
  }

  async function handleUpload() {
    if (!uploadFile || !uploadName.trim() || !uploadCustomerId) {
      message = { type: 'error', text: $_('reports.uploadError') };
      return;
    }

    uploading = true;
    message = null;
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('name', uploadName.trim());
      formData.append('customerId', uploadCustomerId);

      console.log(`[Reports] Uploading report for customer ${uploadCustomerId}`);
      await customerReportsApi.upload(formData);

      message = { type: 'success', text: $_('reports.uploadSuccess') };
      clearUploadForm();
      await loadReports();
    } catch (err) {
      console.error('[Reports] Error uploading report:', err);
      message = { type: 'error', text: err.message };
    } finally {
      uploading = false;
    }
  }

  async function viewReport(reportId) {
    loadingReport = true;
    try {
      console.log(`[Reports] Loading report ${reportId}`);
      const response = await customerReportsApi.get(reportId);
      viewingReport = response;
    } catch (err) {
      console.error('[Reports] Error loading report:', err);
      message = { type: 'error', text: err.message };
    } finally {
      loadingReport = false;
    }
  }

  function closeReportViewer() {
    viewingReport = null;
  }

  async function deleteReport(reportId) {
    try {
      console.log(`[Reports] Deleting report ${reportId}`);
      await customerReportsApi.delete(reportId);
      message = { type: 'success', text: $_('reports.deleteSuccess') };
      deletingReportId = null;
      await loadReports();
    } catch (err) {
      console.error('[Reports] Error deleting report:', err);
      message = { type: 'error', text: err.message };
    }
  }

  function formatDate(dateString) {
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
</script>

<Layout>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div class="flex items-center space-x-4">
        <div class="p-3 bg-linear-to-br from-primary-500 to-primary-600 rounded-2xl shadow-lg">
          <FileText size={28} class="text-white" />
        </div>
        <div>
          <h1 class="text-2xl font-bold text-gray-900 dark:text-white">{$_('reports.title')}</h1>
          <p class="text-sm text-gray-500 dark:text-gray-400">{$_('reports.subtitle')}</p>
        </div>
      </div>
    </div>

    <!-- Message -->
    {#if message}
      <div class="p-4 rounded-lg flex items-center space-x-2 {message.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'}">
        {#if message.type === 'success'}
          <CheckCircle size={20} />
        {:else}
          <AlertCircle size={20} />
        {/if}
        <span>{message.text}</span>
        <button class="ml-auto hover:opacity-70" onclick={() => message = null}>&times;</button>
      </div>
    {/if}

    <!-- Admin: Upload Form -->
    {#if $isAdmin}
      <div class="card p-6">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
          <Upload size={20} class="text-primary-500" />
          <span>{$_('reports.upload')}</span>
        </h3>

        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <!-- Report Name -->
          <div>
            <label class="label">{$_('reports.name')}</label>
            <input
              type="text"
              class="input w-full"
              placeholder={$_('reports.namePlaceholder')}
              bind:value={uploadName}
            />
          </div>

          <!-- Customer Dropdown -->
          <div>
            <label class="label">{$_('reports.selectCustomer')}</label>
            <select
              class="input w-full"
              bind:value={uploadCustomerId}
              disabled={loadingCustomers}
            >
              <option value="">{$_('reports.selectCustomerPlaceholder')}</option>
              {#each customers as customer}
                <option value={customer.id}>{customer.companyName || `${customer.name} ${customer.surname}`}</option>
              {/each}
            </select>
          </div>

          <!-- File Input -->
          <div>
            <label class="label">{$_('reports.selectFile')}</label>
            <input
              type="file"
              accept=".md,text/markdown"
              class="input w-full file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 dark:file:bg-primary-900/30 file:text-primary-700 dark:file:text-primary-300 hover:file:bg-primary-100 dark:hover:file:bg-primary-900/50"
              bind:this={fileInputRef}
              onchange={handleFileChange}
            />
          </div>

          <!-- Upload Button -->
          <div class="flex items-end">
            <button
              class="btn btn-primary w-full flex items-center justify-center space-x-2"
              onclick={handleUpload}
              disabled={uploading || !uploadFile || !uploadName.trim() || !uploadCustomerId}
            >
              {#if uploading}
                <Loader size={18} class="animate-spin" />
              {:else}
                <Upload size={18} />
              {/if}
              <span>{$_('reports.uploadButton')}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Admin: Filter -->
      <div class="flex items-center space-x-4">
        <div class="flex items-center space-x-2">
          <Filter size={18} class="text-gray-500" />
          <select
            class="input"
            bind:value={filterCustomerId}
            onchange={handleFilterChange}
          >
            <option value="">{$_('reports.allCustomers')}</option>
            {#each customers as customer}
              <option value={customer.id}>{customer.companyName || `${customer.name} ${customer.surname}`}</option>
            {/each}
          </select>
        </div>
      </div>
    {/if}

    <!-- Reports Table/List -->
    {#if loading}
      <div class="card p-12 text-center">
        <Loader size={48} class="mx-auto text-primary-500 animate-spin mb-4" />
        <p class="text-gray-500 dark:text-gray-400">{$_('common.loading')}</p>
      </div>
    {:else if reports.length === 0}
      <div class="card p-12 text-center">
        <FileText size={48} class="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
        <p class="text-gray-500 dark:text-gray-400">{$_('reports.noReports')}</p>
      </div>
    {:else}
      <div class="card overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('reports.name')}</th>
                {#if $isAdmin}
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('reports.customer')}</th>
                {/if}
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('reports.date')}</th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('common.actions')}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              {#each reports as report (report.id)}
                <tr class="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td class="px-4 py-4">
                    <div class="flex items-center space-x-3">
                      <FileText size={20} class="text-primary-500 flex-shrink-0" />
                      <span class="font-medium text-gray-900 dark:text-white">{report.name}</span>
                    </div>
                  </td>
                  {#if $isAdmin}
                    <td class="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
                      {report.customerName || report.customerId}
                    </td>
                  {/if}
                  <td class="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                    <div class="flex items-center space-x-1">
                      <Calendar size={14} />
                      <span>{formatDate(report.createdAt)}</span>
                    </div>
                  </td>
                  <td class="px-4 py-4 text-right">
                    <div class="flex items-center justify-end space-x-2">
                      <!-- View Button -->
                      <button
                        class="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
                        onclick={() => viewReport(report.id)}
                        title={$_('reports.view')}
                        disabled={loadingReport}
                      >
                        {#if loadingReport}
                          <Loader size={18} class="animate-spin" />
                        {:else}
                          <Eye size={18} />
                        {/if}
                      </button>

                      <!-- Delete Button (Admin only) -->
                      {#if $isAdmin}
                        {#if deletingReportId === report.id}
                          <div class="flex items-center space-x-1">
                            <button
                              class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors text-xs font-medium"
                              onclick={() => deleteReport(report.id)}
                            >
                              {$_('common.confirm')}
                            </button>
                            <button
                              class="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                              onclick={() => deletingReportId = null}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        {:else}
                          <button
                            class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                            onclick={() => deletingReportId = report.id}
                            title={$_('common.delete')}
                          >
                            <Trash2 size={18} />
                          </button>
                        {/if}
                      {/if}
                    </div>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
    {/if}
  </div>

  <!-- Report Viewer Modal -->
  <ReportViewer report={viewingReport} onClose={closeReportViewer} />
</Layout>
